import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ContentProvider, useContent } from "@/lib/content";
import AdminPage from "@/pages/AdminPage";
import {
  Menu as MenuIcon,
  X as XIcon,
  MapPin,
  Phone,
  Mail,
  Clock,
  Facebook,
  ArrowUpRight,
  Quote,
  ChevronDown,
} from "lucide-react";

// ============ LANGUAGE CONTEXT ============
const LangContext = createContext({ lang: "hu", setLang: () => {}, t: {} });
const useLang = () => useContext(LangContext);

// ============ TOP STATUS BAR ============
const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]; // JS getDay() order

const useNowInBudapest = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000); // refresh each minute
    return () => clearInterval(id);
  }, []);
  return now;
};

const parseHHMM = (s) => {
  const [h, m] = (s || "0:00").split(":").map((n) => parseInt(n, 10));
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

const computeStatus = (schedule, now) => {
  // Get Budapest time
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Budapest",
    hour12: false,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  const parts = fmt.formatToParts(now);
  const wd = parts.find((p) => p.type === "weekday")?.value || "Mon";
  const hh = parts.find((p) => p.type === "hour")?.value || "00";
  const mm = parts.find((p) => p.type === "minute")?.value || "00";
  const minutesNow = parseInt(hh, 10) * 60 + parseInt(mm, 10);
  const dayKey = ({ Sun: "sun", Mon: "mon", Tue: "tue", Wed: "wed", Thu: "thu", Fri: "fri", Sat: "sat" })[wd] || "mon";
  const todayRange = (schedule || {})[dayKey] || "";
  if (!todayRange.trim()) return { open: false, today: "" };
  const [openStr, closeStr] = todayRange.split(/[-–—]/).map((s) => s.trim());
  if (!openStr || !closeStr) return { open: false, today: todayRange };
  const o = parseHHMM(openStr);
  const c = parseHHMM(closeStr);
  // Handle past-midnight close (e.g. 24:00 = 24*60)
  const isOpen = minutesNow >= o && minutesNow < c;
  return { open: isOpen, today: `${openStr} – ${closeStr}` };
};

const TopBar = () => {
  const { t } = useLang();
  const now = useNowInBudapest();
  const tb = t.topbar;
  if (!tb || tb.enabled === false) return null;
  const labels = tb.labels || { open: "NYITVA", closed: "ZÁRVA", today: "MA" };
  const { open, today } = computeStatus(tb.schedule, now);
  const msg = (tb.message || "").trim();
  return (
    <div
      data-testid="topbar"
      className="fixed top-0 left-0 right-0 z-[60] bg-black border-b border-white/10"
    >
      <div className="px-4 md:px-12 lg:px-16 py-2 flex items-center justify-between gap-4 text-[11px] sm:text-xs">
        <div className="flex items-center gap-3 min-w-0">
          <span className="flex items-center gap-2 whitespace-nowrap">
            <span
              data-testid="topbar-status-dot"
              className={`inline-block w-2 h-2 rounded-full ${open ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" : "bg-red-500"}`}
              aria-hidden
            />
            <span
              data-testid="topbar-status-label"
              className={`font-heading uppercase tracking-widest ${open ? "text-emerald-400" : "text-red-400"}`}
            >
              {open ? labels.open : labels.closed}
            </span>
          </span>
          {today && (
            <span data-testid="topbar-today" className="hidden sm:inline text-ink-muted uppercase tracking-widest">
              · {labels.today} {today}
            </span>
          )}
        </div>
        {msg && (
          <div
            data-testid="topbar-message"
            className="truncate font-body text-brand uppercase tracking-widest"
            title={msg}
          >
            {msg}
          </div>
        )}
      </div>
    </div>
  );
};

// ============ NAVBAR ============
const NAV_ITEMS = [
  { id: "about", key: "about" },
  { id: "weekly", key: "menu" },
  { id: "menus", key: "menus" },
  { id: "gallery", key: "gallery" },
  { id: "reviews", key: "reviews" },
  { id: "contact", key: "contact" },
];

const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const topbarEnabled = t.topbar && t.topbar.enabled !== false;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav
      data-testid="main-nav"
      style={{ top: topbarEnabled ? "var(--topbar-h, 36px)" : 0 }}
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-bg/80 border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-16 py-5 flex items-center justify-between">
        <button
          data-testid="logo-button"
          onClick={() => go("hero")}
          className="font-heading text-2xl md:text-3xl tracking-tight uppercase text-white hover:opacity-90 transition"
        >
          FACE <span className="text-brand">ROOM</span>
        </button>

        <div className="hidden lg:flex items-center gap-10">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-link-${item.id}`}
              onClick={() => go(item.id)}
              className="font-body text-[12px] font-semibold uppercase tracking-[0.2em] text-white/80 hover:text-brand transition-colors"
            >
              {t.nav?.[item.key]}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-5">
          <div className="flex items-center gap-2 font-body text-xs font-bold uppercase tracking-widest">
            <button
              data-testid="lang-toggle-hu"
              onClick={() => setLang("hu")}
              className={lang === "hu" ? "text-brand" : "text-ink-muted hover:text-white"}
            >
              HU
            </button>
            <span className="text-ink-dim">/</span>
            <button
              data-testid="lang-toggle-en"
              onClick={() => setLang("en")}
              className={lang === "en" ? "text-brand" : "text-ink-muted hover:text-white"}
            >
              EN
            </button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 -mr-2 text-white"
            aria-label="Toggle menu"
          >
            {open ? <XIcon size={26} /> : <MenuIcon size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-bg/95 backdrop-blur-xl border-t border-white/5">
          <div className="px-6 py-8 flex flex-col gap-6">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                data-testid={`mobile-nav-link-${item.id}`}
                onClick={() => go(item.id)}
                className="font-heading text-3xl uppercase tracking-tight text-white text-left hover:text-brand transition-colors"
              >
                {t.nav?.[item.key]}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

// ============ HERO ============
const Hero = () => {
  const { t } = useLang();
  const heroBg = (t.gallery?.images && t.gallery.images[3]) || "";
  const h = t.hero || {};
  return (
    <section
      id="hero"
      data-testid="section-hero"
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: `url(${heroBg})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-bg via-bg/70 to-bg" />

      <div className="relative px-6 md:px-12 lg:px-16 w-full max-w-[1600px] mx-auto">
        <p
          data-testid="hero-eyebrow"
          className="font-body text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-brand mb-8 reveal"
        >
          {h.eyebrow}
        </p>

        <h1 className="font-heading uppercase leading-[1] text-white">
          <span className="block text-6xl sm:text-7xl md:text-8xl lg:text-[10rem] xl:text-[12rem] reveal pt-2" style={{ animationDelay: "0.1s" }}>
            {h.line1}
          </span>
          <span className="block text-6xl sm:text-7xl md:text-8xl lg:text-[10rem] xl:text-[12rem] text-brand reveal pt-2" style={{ animationDelay: "0.2s" }}>
            {h.line2}
          </span>
          <span className="block text-6xl sm:text-7xl md:text-8xl lg:text-[10rem] xl:text-[12rem] reveal pt-2" style={{ animationDelay: "0.3s" }}>
            {h.line3}
          </span>
        </h1>

        <p
          className="mt-10 max-w-2xl font-body text-lg sm:text-xl text-white/75 leading-relaxed reveal"
          style={{ animationDelay: "0.5s" }}
        >
          {h.lead}
        </p>

        <div className="mt-10 flex flex-wrap gap-4 reveal" style={{ animationDelay: "0.7s" }}>
          <button
            data-testid="hero-cta-primary"
            onClick={() => document.getElementById("weekly")?.scrollIntoView({ behavior: "smooth" })}
            className="bg-brand text-black font-heading uppercase text-lg md:text-xl tracking-wide px-10 py-5 rounded-full hover:bg-brand-hover hover:scale-[1.03] transition-all duration-300"
          >
            {h.cta}
          </button>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-ink-muted animate-bounce hidden md:block">
          <ChevronDown size={28} />
        </div>
      </div>
    </section>
  );
};

// ============ MARQUEE ============
const Marquee = () => {
  const { t } = useLang();
  const text = `${t.marquee || ""}  ${t.marquee || ""}  `;
  return (
    <div data-testid="marquee" className="overflow-hidden py-8 border-y border-white/5 bg-bg">
      <div className="flex animate-marquee whitespace-nowrap">
        <span className="font-heading text-5xl md:text-7xl uppercase tracking-tight text-stroke px-4">{text}</span>
        <span className="font-heading text-5xl md:text-7xl uppercase tracking-tight text-stroke px-4" aria-hidden>{text}</span>
      </div>
    </div>
  );
};

// ============ ABOUT ============
const About = () => {
  const { t } = useLang();
  const a = t.about || {};
  const aboutImage = (t.gallery?.images && t.gallery.images[4]) || "";
  return (
    <section id="about" data-testid="section-about" className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
      <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-7">
          <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-brand mb-6">{a.eyebrow}</p>
          <h2 className="font-heading uppercase text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.05] text-white mb-10 pt-2">
            {a.headline}
          </h2>
          <div className="space-y-6 font-body text-lg text-ink-muted leading-relaxed max-w-2xl">
            <p>{a.p1}</p>
            <p>{a.p2}</p>
            <p>{a.p3}</p>
            <p className="text-brand font-semibold pt-4">{a.sign}</p>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="relative aspect-[3/4] overflow-hidden">
            <img
              src={aboutImage}
              alt="Faceroom interior"
              className="absolute inset-0 w-full h-full object-cover grayscale-[20%] hover:grayscale-0 hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-bg/80 to-transparent" />
          </div>
          <div className="mt-6 grid grid-cols-2 gap-6">
            <div className="border-l-2 border-brand pl-4 py-2">
              <div className="font-heading text-4xl text-white">10+</div>
              <div className="font-body text-xs uppercase tracking-widest text-ink-muted">Év</div>
            </div>
            <div className="border-l-2 border-brand pl-4 py-2">
              <div className="font-heading text-xl md:text-2xl uppercase text-white leading-tight">Megszámlálhatatlan</div>
              <div className="font-body text-xs uppercase tracking-widest text-ink-muted mt-1">Élmények</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ WEEKLY MENU ============
const WeeklyMenu = () => {
  const { t } = useLang();
  const w = t.weekly || {};
  return (
    <section
      id="weekly"
      data-testid="section-weekly"
      className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-surface/50 border-y border-white/5"
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-brand mb-6">{w.eyebrow}</p>
            <h2 className="font-heading uppercase text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.05] text-white pt-2">
              {w.headline}
            </h2>
          </div>
          <p className="font-body text-base text-ink-muted max-w-md md:text-right">{w.sub}</p>
        </div>

        <div
          data-testid="extra-menu-card"
          className="mb-12 p-6 md:p-8 border-l-4 border-brand bg-surface flex flex-col md:flex-row md:items-center md:justify-between gap-3"
        >
          <div>
            <div className="font-body text-xs uppercase tracking-[0.3em] text-brand mb-2">{w.extraLabel}</div>
            <div className="font-heading text-2xl md:text-3xl uppercase text-white">{w.extraDish}</div>
          </div>
        </div>

        <div className="space-y-2">
          {(w.days || []).map((day, idx) => (
            <DayRow key={idx} day={day} w={w} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

const DayRow = ({ day, w, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div
      data-testid={`day-row-${idx}`}
      className={`border border-white/5 ${open ? "bg-surface" : "bg-bg/40"} transition-colors`}
    >
      <button
        data-testid={`day-toggle-${idx}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 md:px-10 py-6 group"
      >
        <div className="flex items-baseline gap-6">
          <span className="font-heading text-xs md:text-sm uppercase tracking-widest text-ink-dim w-6">
            0{idx + 1}
          </span>
          <span className="font-heading text-2xl md:text-4xl uppercase text-white group-hover:text-brand transition-colors text-left">
            {day.date}
          </span>
        </div>
        <ChevronDown
          size={24}
          className={`text-ink-muted transition-transform duration-300 ${open ? "rotate-180 text-brand" : ""}`}
        />
      </button>
      {open && (
        <div className="px-6 md:px-10 pb-8 grid md:grid-cols-2 gap-6 md:gap-12 animate-fade-up">
          <div>
            <div className="font-body text-xs uppercase tracking-[0.25em] text-brand mb-4">
              {w.soup} · 800 Ft
            </div>
            <ul className="space-y-3">
              {(day.soups || []).map((s, i) => (
                <li key={i} className="font-body text-base md:text-lg text-white border-b border-white/5 pb-3">
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="font-body text-xs uppercase tracking-[0.25em] text-brand mb-4">
              {w.main} · 2600 Ft
            </div>
            <ul className="space-y-3">
              {(day.mains || []).map((m, i) => (
                <li key={i} className="font-body text-base md:text-lg text-white border-b border-white/5 pb-3">
                  {m}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ PDF MENUS ============
const PdfMenus = () => {
  const { t } = useLang();
  const m = t.menus || {};
  const cards = [
    { key: "food", title: m.food?.title, sub: m.food?.sub, cta: m.food?.cta, href: m.food?.url, img: t.gallery?.images?.[4] },
    { key: "drinks", title: m.drinks?.title, sub: m.drinks?.sub, cta: m.drinks?.cta, href: m.drinks?.url, img: t.gallery?.images?.[3] },
  ];
  return (
    <section id="menus" data-testid="section-menus" className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
      <div className="max-w-[1600px] mx-auto">
        <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-brand mb-6">{m.eyebrow}</p>
        <h2 className="font-heading uppercase text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.05] text-white mb-16 pt-2">
          {m.headline}
        </h2>
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {cards.map((c) => (
            <a
              key={c.key}
              data-testid={`pdf-card-${c.key}`}
              href={c.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative aspect-[4/5] md:aspect-[5/6] overflow-hidden bg-surface border border-white/5 hover:border-brand transition-colors"
            >
              <img
                src={c.img}
                alt={c.title}
                className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/70 to-transparent" />
              <div className="relative h-full flex flex-col justify-between p-8 md:p-12">
                <div>
                  <div className="font-heading text-6xl md:text-8xl text-white group-hover:text-brand transition-colors">
                    {c.title}
                  </div>
                  <p className="mt-6 font-body text-base text-ink-muted max-w-md">{c.sub}</p>
                </div>
                <div className="flex items-center gap-3 font-body text-sm uppercase tracking-[0.25em] text-white group-hover:text-brand transition-colors">
                  <span>{c.cta}</span>
                  <ArrowUpRight
                    size={20}
                    className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ GALLERY ============
const GALLERY_SPANS = ["row-span-2", "", "row-span-2", "", ""];

const Gallery = () => {
  const { t } = useLang();
  const g = t.gallery || {};
  const images = g.images || [];
  return (
    <section
      id="gallery"
      data-testid="section-gallery"
      className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-surface/30 border-y border-white/5"
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-brand mb-6">{g.eyebrow}</p>
            <h2 className="font-heading uppercase text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.05] text-white pt-2">
              {g.headline}
            </h2>
          </div>
          <p className="font-body text-base text-ink-muted max-w-md">{g.sub}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-3 md:gap-4">
          {images.map((src, idx) => (
            <div
              key={idx}
              data-testid={`gallery-item-${idx}`}
              className={`group relative overflow-hidden bg-surface ${GALLERY_SPANS[idx] || ""}`}
            >
              <img
                src={src}
                alt={`Faceroom ${idx + 1}`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover grayscale-[15%] group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-bg/20 group-hover:bg-transparent transition-all duration-500" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ REVIEWS ============
const Reviews = () => {
  const { t } = useLang();
  const r = t.reviews || {};
  return (
    <section id="reviews" data-testid="section-reviews" className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
      <div className="max-w-[1600px] mx-auto">
        <div className="mb-16">
          <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-brand mb-6">{r.eyebrow}</p>
          <h2 className="font-heading uppercase text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.05] text-white pt-2">
            {r.headline}
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {(r.items || []).map((item, i) => (
            <article
              key={i}
              data-testid={`review-card-${i}`}
              className="relative p-8 bg-surface border border-white/5 hover:border-brand/40 transition-colors group"
            >
              <Quote size={36} className="text-brand mb-6 opacity-90" strokeWidth={1.5} />
              <p className="font-body text-base md:text-lg text-white leading-relaxed mb-8">
                &ldquo;{item.text}&rdquo;
              </p>
              <div className="flex items-center gap-4 pt-6 border-t border-white/5">
                <div className="w-10 h-10 rounded-full bg-brand text-black font-heading text-sm flex items-center justify-center">
                  {(item.name || "").split(" ").map((p) => p[0]).join("")}
                </div>
                <div>
                  <div className="font-body text-sm font-semibold text-white">{item.name}</div>
                  <div className="font-body text-xs uppercase tracking-widest text-ink-muted">{item.city}</div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ CONTACT ============
const Contact = () => {
  const { t } = useLang();
  const c = t.contact || {};
  return (
    <section
      id="contact"
      data-testid="section-contact"
      className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-surface/40 border-t border-white/5"
    >
      <div className="max-w-[1600px] mx-auto">
        <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-brand mb-6">{c.eyebrow}</p>
        <h2 className="font-heading uppercase text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9] text-white mb-16 max-w-4xl">
          {c.headline}
        </h2>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5 space-y-10">
            <ContactItem
              icon={<MapPin size={22} className="text-brand" />}
              label={c.address}
              value={c.addressValue}
              testid="contact-address"
              href="https://maps.google.com/?q=Győr+Pálffy+u.+2"
            />
            <ContactItem
              icon={<Phone size={22} className="text-brand" />}
              label={c.phone}
              value={c.phoneValue}
              testid="contact-phone"
              href="tel:+3696823105"
              big
            />
            <ContactItem
              icon={<Mail size={22} className="text-brand" />}
              label={c.email}
              value={c.emailValue}
              testid="contact-email"
              href="mailto:info@faceroom.hu"
            />
            <ContactItem
              icon={<Facebook size={22} className="text-brand" />}
              label={c.facebook}
              value="/faceroom.gyor"
              testid="contact-facebook"
              href="https://www.facebook.com/faceroom.gyor"
            />
            <div className="flex items-start gap-4">
              <div className="mt-1">
                <span className="block w-[22px] h-[22px] text-brand text-xs font-heading">⌖</span>
              </div>
              <div>
                <div className="font-body text-xs uppercase tracking-[0.25em] text-ink-muted mb-1">{c.gps}</div>
                <div className="font-body text-sm text-white">{c.gpsValue}</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-8">
              <Clock size={22} className="text-brand" />
              <div className="font-body text-xs uppercase tracking-[0.25em] text-ink-muted">{c.hours}</div>
            </div>
            <div data-testid="opening-hours" className="space-y-1">
              {(c.hoursTable || []).map((row, i) => {
                const isClosed = (row.t || "").toLowerCase().includes("zárva") || (row.t || "").toLowerCase().includes("closed");
                return (
                  <div key={i} className="flex items-baseline justify-between gap-6 py-5 border-b border-white/5">
                    <span className="font-heading text-xl md:text-3xl uppercase text-white">{row.d}</span>
                    <span className={`font-heading text-xl md:text-3xl uppercase ${isClosed ? "text-ink-dim" : "text-brand"}`}>
                      {row.t}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 aspect-[16/9] overflow-hidden border border-white/5">
              <iframe
                title="Faceroom Map"
                src="https://www.google.com/maps?q=Győr+Pálffy+u.+2&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg) saturate(0.6)" }}
                loading="lazy"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ContactItem = ({ icon, label, value, href, big, testid }) => (
  <a
    href={href}
    target={href?.startsWith("http") ? "_blank" : undefined}
    rel="noopener noreferrer"
    data-testid={testid}
    className="flex items-start gap-4 group"
  >
    <div className="mt-1">{icon}</div>
    <div>
      <div className="font-body text-xs uppercase tracking-[0.25em] text-ink-muted mb-1">{label}</div>
      <div
        className={`${
          big ? "font-heading text-3xl md:text-5xl tracking-tight" : "font-body text-base md:text-lg"
        } text-white group-hover:text-brand transition-colors`}
      >
        {value}
      </div>
    </div>
  </a>
);

// ============ FOOTER ============
const Footer = () => {
  const { t } = useLang();
  const f = t.footer || {};
  return (
    <footer data-testid="footer" className="px-6 md:px-12 lg:px-16 py-16 bg-bg border-t border-white/5">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <div>
            <div className="font-heading text-4xl md:text-5xl uppercase text-white">
              FACE <span className="text-brand">ROOM</span>
            </div>
            <div className="mt-3 font-body text-sm uppercase tracking-[0.25em] text-ink-muted">
              {f.tagline}
            </div>
          </div>
          <div className="flex items-center gap-6">
            <a data-testid="footer-facebook" href="https://www.facebook.com/faceroom.gyor" target="_blank" rel="noopener noreferrer" className="text-white hover:text-brand transition-colors" aria-label="Facebook">
              <Facebook size={22} />
            </a>
            <a data-testid="footer-email" href="mailto:info@faceroom.hu" className="text-white hover:text-brand transition-colors" aria-label="Email">
              <Mail size={22} />
            </a>
            <a data-testid="footer-phone" href="tel:+3696823105" className="text-white hover:text-brand transition-colors" aria-label="Phone">
              <Phone size={22} />
            </a>
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col md:flex-row md:items-center md:justify-between gap-3 font-body text-xs uppercase tracking-widest text-ink-dim">
          <div>© {new Date().getFullYear()} Faceroom. {f.rights}</div>
          <div>Győr · Pálffy u. 2.</div>
        </div>
      </div>
    </footer>
  );
};

// ============ HOME ============
const Home = () => {
  const { content } = useContent();
  const [lang, setLang] = useState("hu");
  const t = content[lang] || content.hu;

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      <div className="bg-bg text-white min-h-screen">
        <TopBar />
        <Navbar />
        <Hero />
        <Marquee />
        <About />
        <WeeklyMenu />
        <PdfMenus />
        <Gallery />
        <Reviews />
        <Contact />
        <Footer />
      </div>
    </LangContext.Provider>
  );
};

// ============ APP ============
function App() {
  return (
    <div className="App">
      <ContentProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
        </BrowserRouter>
      </ContentProvider>
    </div>
  );
}

export default App;
